/** 'template' (Oct 2026) = a saved starting point in the admin page; never shown, never sent. */
export const ANNOUNCEMENT_STATUSES = ['draft', 'published', 'template'];
/** Dismiss key stored in `user.dismissedAnnouncements`. */
export const announcementDismissKey = (id) => `ann:${id}`;
/** Does `user` belong to the audience? */
export function announcementTargets(audience, user) {
    if (audience.all)
        return true;
    const ent = (user.employmentEntity ?? '').trim().toLowerCase();
    const team = (user.team ?? '').trim().toLowerCase();
    const email = (user.email ?? '').trim().toLowerCase();
    const tools = user.tools ?? {};
    return ((ent !== '' && audience.entities.some((e) => e.trim().toLowerCase() === ent)) ||
        (team !== '' && audience.teams.some((t) => t.trim().toLowerCase() === team)) ||
        (audience.tools ?? []).some((slug) => tools[slug]?.enabled === true) ||
        (email !== '' && (audience.emails ?? []).some((e) => e.trim().toLowerCase() === email)));
}
/** Live at `today` (YYYY-MM-DD) and, when given, `nowTime` (HH:MM, Zurich): started and not past the last day. */
export function announcementIsLive(a, today, nowTime) {
    if (a.status !== 'published' || a.expiresAt < today || a.publishAt > today)
        return false;
    if (a.publishAt === today && nowTime && a.publishTime && a.publishTime > nowTime)
        return false;
    return true;
}
export const announcementChannels = (a) => a.channels ?? { home: true, popup: false, email: true };
/** The blocks (older posts: one block from `body`). */
export function announcementBlocks(a) {
    return a.blocks?.length ? a.blocks : [{ id: 'main', body: a.body, imageUrl: null, audience: null }];
}
/** The blocks this person sees (block audience on top of the announcement audience). */
export function announcementBlocksFor(a, user) {
    return announcementBlocks(a).filter((b) => !b.audience || announcementTargets(b.audience, user));
}
/** Pop-up dismiss key (separate from Home's "Got it", so closing the pop-up keeps it on Home). */
export const announcementPopupKey = (id) => `annpop:${id}`;
/** Tools that can be an audience ("people with …"). */
export const ANNOUNCEMENT_TOOL_AUDIENCES = [
    { slug: 'absence', label: 'Absence' },
    { slug: 'timeTracking', label: 'Time Tracking' },
    { slug: 'expenses', label: 'Expenses' },
    { slug: 'creditCards', label: 'Credit Cards' },
    { slug: 'invoices', label: 'Invoices' },
];
/** Per-person placeholders, filled server-side with the numbers the person sees on their own pages. */
export const ANNOUNCEMENT_PLACEHOLDERS = [
    { key: 'firstName', label: 'First name', example: 'Anna' },
    { key: 'fullName', label: 'Full name', example: 'Anna Schmidt' },
    { key: 'ptoRemaining', label: 'Vacation days left', example: '7.5' },
    { key: 'ptoEntitlement', label: 'Vacation entitlement (days)', example: '25' },
    { key: 'ptoTaken', label: 'Vacation days taken', example: '15' },
    { key: 'ptoPlanned', label: 'Vacation days planned (approved + requested, not yet taken)', example: '2.5' },
    { key: 'leaveYearEnd', label: 'End of the leave year', example: '31 Dec 2026' },
    { key: 'hourBalance', label: 'Hour balance (as of yesterday)', example: '+12:30 h' },
];
/** Replace {{key}} tokens; unknown keys stay visible so a typo is noticed in the preview. */
export function fillAnnouncementPlaceholders(text, values) {
    return text.replace(/\{\{\s*([A-Za-z]+)\s*\}\}/g, (m, k) => (k in values ? values[k] : m));
}
/** Placeholder keys used anywhere in the title or blocks (to compute only what is needed). */
export function announcementPlaceholdersUsed(title, blocks) {
    const used = new Set();
    for (const t of [title, ...blocks.map((b) => b.body)])
        for (const m of t.matchAll(/\{\{\s*([A-Za-z]+)\s*\}\}/g))
            used.add(m[1]);
    return used;
}
/** Human audience line: "Everyone" / "DAAily platforms GmbH · Sales team". */
export function announcementAudienceLabel(a) {
    if (a.all)
        return 'Everyone';
    const toolLabel = (s) => ANNOUNCEMENT_TOOL_AUDIENCES.find((t) => t.slug === s)?.label ?? s;
    const n = (a.emails ?? []).length;
    const parts = [...a.entities, ...a.teams.map((t) => `${t} team`), ...(a.tools ?? []).map((t) => `${toolLabel(t)} users`), ...(n ? [`${n} ${n === 1 ? 'person' : 'people'}`] : [])];
    return parts.length ? parts.join(' · ') : 'Nobody (no audience set)';
}
//# sourceMappingURL=announcements.js.map